import * as _ from 'lodash-es';
import { BlockEmbed } from 'quill/blots/block';
import type QuillScroll from 'quill/blots/scroll';
import { Parchment } from 'quill/core';
import React from 'react';
import { createPortal } from 'react-dom';
import { MOD_KEY } from '../../constants/os';
import fullOrgUrl from '../../utils/url/fullOrgUrl';
import postPath from '../../utils/url/postPath';
import { LINK_TYPES, LINK_TYPE_ATTRIBUTE } from '../constants';
import CardContent from '../embed/CardContent';
import * as styles from './Card.module.scss';
import Comment from './Comment';
import Diff from './Diff';
import Reaction from './Reaction';
import Scroll from './Scroll';
import UserSequence from './UserSequence';
import VersionChange from './VersionChange';

export interface CardValue {
  id: string;
  type: 'post';
  params?: string;
}

class Card extends BlockEmbed {
  static blotName = 'card';
  static className = 'ql-card';
  static tagName = 'div';

  static shouldLazyLoad = true;

  static create(value: CardValue) {
    const node = super.create(value) as HTMLDivElement;
    node.setAttribute('data-id', value.id);
    if (value.params) {
      node.setAttribute('data-params', value.params);
    }
    // @ts-expect-error
    node.setAttribute('contenteditable', false);
    node.classList.add(styles.container);
    return node;
  }

  static value(domNode: Element) {
    const params = domNode.getAttribute('data-params');
    return {
      type: 'post',
      id: domNode.getAttribute('data-id'),
      ...(params ? { params } : null),
    };
  }

  id: string;

  constructor(scroll: QuillScroll, domNode: HTMLElement) {
    super(scroll, domNode);
    this.id = _.uniqueId();

    const openInNewTab = () => {
      const url = this.getFullUrl();
      if (url) {
        window.open(url, '_blank', 'noopener');
      }
    };

    domNode.addEventListener('click', (event) => {
      if (!scroll.isEnabled()) return;

      if (event[MOD_KEY] || event.detail === 2) {
        openInNewTab();
      }
    });
  }

  attach() {
    super.attach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_MOUNT, this);
  }

  detach() {
    super.detach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_UNMOUNT, this);
  }

  format(name: string, value: unknown) {
    if (
      [
        Comment.attrName,
        Reaction.attrName,
        Diff.attrName,
        VersionChange.attrName,
        UserSequence.attrName,
      ].includes(name)
    ) {
      const attributor = this.scroll.query(name, Parchment.Scope.ATTRIBUTE);
      if (attributor) {
        // @ts-expect-error
        this.attributes.attribute(attributor, value);
      }
    } else {
      super.format(name, value);
    }
  }

  formats() {
    return this.attributes.values();
  }

  getFullUrl() {
    const {
      card: { id, params },
    } = this.value();
    const paramsObject = new URLSearchParams(params);
    const shareSecret = paramsObject.get('shr') ?? undefined;

    return fullOrgUrl(
      postPath(
        {
          id,
          title: '',
        },
        { shareSecret },
      ),
    );
  }

  html() {
    const url = this.getFullUrl();
    return `<a ${LINK_TYPE_ATTRIBUTE}="${LINK_TYPES.card}" href=${url}>${url}</a>`;
  }

  getReferenceNodeForBounds() {
    return this.domNode.querySelector('.card-content') || this.domNode;
  }

  renderPortal() {
    const { id, params } = this.value().card;
    return createPortal(<CardContent id={id} params={params} />, this.domNode);
  }
}

export default Card;
